import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { XIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import {
  type OverlayDismissHandlerProps,
  type RadixDismissEvent,
} from '@/components/ui/overlay-dismiss-handlers'

export type { RadixDismissEvent }

const DialogDismissHandlersContext = React.createContext<
  React.MutableRefObject<OverlayDismissHandlerProps>
>({ current: {} })

function Dialog({ onOpenChange, ...props }: DialogPrimitive.Root.Props) {
  const dismissHandlersRef = React.useRef<OverlayDismissHandlerProps>({})

  const handleOpenChange = React.useCallback(
    (open: boolean, details: DialogPrimitive.Root.ChangeEventDetails) => {
      if (!open) {
        const event: RadixDismissEvent = {
          preventDefault: () => {
            details.cancel()
          },
          target: details.event?.target ?? null,
        }

        if (details.reason === 'outside-press') {
          dismissHandlersRef.current.onPointerDownOutside?.(event)
          dismissHandlersRef.current.onInteractOutside?.(event)
        } else if (details.reason === 'focus-out') {
          dismissHandlersRef.current.onFocusOutside?.(event)
          dismissHandlersRef.current.onInteractOutside?.(event)
        } else if (details.reason === 'escape-key') {
          dismissHandlersRef.current.onEscapeKeyDown?.(event)
        }

        if (details.isCanceled) {
          return
        }
      }

      onOpenChange?.(open, details)
    },
    [onOpenChange]
  )

  return (
    <DialogDismissHandlersContext.Provider value={dismissHandlersRef}>
      <DialogPrimitive.Root
        data-slot='dialog'
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogDismissHandlersContext.Provider>
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot='dialog-overlay'
      className={cn(
        'fixed inset-0 z-50 bg-black/50 opacity-100 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0',
        className
      )}
      {...props}
    />
  )
}

type DialogContentProps = DialogPrimitive.Popup.Props &
  OverlayDismissHandlerProps & {
    showCloseButton?: boolean
  }

function DialogContent({
  className,
  children,
  showCloseButton = true,
  onPointerDownOutside,
  onInteractOutside,
  onFocusOutside,
  onEscapeKeyDown,
  ...props
}: DialogContentProps) {
  const dismissHandlersRef = React.useContext(DialogDismissHandlersContext)

  React.useLayoutEffect(() => {
    dismissHandlersRef.current = {
      onPointerDownOutside,
      onInteractOutside,
      onFocusOutside,
      onEscapeKeyDown,
    }
    return () => {
      dismissHandlersRef.current = {}
    }
  }, [
    dismissHandlersRef,
    onPointerDownOutside,
    onInteractOutside,
    onFocusOutside,
    onEscapeKeyDown,
  ])

  return (
    <DialogPortal data-slot='dialog-portal'>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot='dialog-content'
        className={cn(
          'fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-background p-6 shadow-lg outline-none transition-[transform,opacity] duration-200 ease-out data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 sm:max-w-lg',
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot='dialog-close'
            className="absolute end-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-open:bg-accent data-open:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className='sr-only'>Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-header'
      className={cn('flex flex-col gap-2 text-center sm:text-start', className)}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
